import type { ComboVariationPricing, ComboVariationWithPricing, PropostaItem } from '@/types'
import { formatCurrency } from '@/utils/format-currency'
import { getComboDurationFromItems } from '@/utils/dealCalculations'

export type ResolvedComboVariationPricing = {
  recorrente: boolean
  total: number
  entry: number
  installments: number
  installmentValue: number
  setupTotal: number
}

export function inferComboPricingModel(variation: ComboVariationWithPricing): 'recorrente' | 'setup' {
  if (variation.pricingModel === 'recorrente' || variation.pricingModel === 'setup') {
    return variation.pricingModel
  }
  const hasRecurring = variation.items.some((i) => i.tipo === 'plano' || i.tipo === 'servico')
  const hasAvulso = variation.items.some((i) => i.tipo === 'avulso')
  if (hasRecurring) return 'recorrente'
  if (hasAvulso) return 'setup'
  return 'recorrente'
}

/** Resolve totais do combo para contrato / Anexo 1 (mensalidade × parcelas + entrada + setup). */
export function resolveComboVariationPricing(
  variation: ComboVariationWithPricing,
): ResolvedComboVariationPricing {
  const explicit = variation.pricing
  const recorrente = explicit?.recorrente ?? inferComboPricingModel(variation) === 'recorrente'
  const installments =
    explicit?.installments ??
    variation.duration ??
    getComboDurationFromItems(variation.items)
  const setupTotal = variation.valorSetup ?? 0
  const entry = Math.max(0, Number(explicit?.entry ?? 0))

  let installmentValue = Number(
    explicit?.installmentValue ?? variation.valorFinal ?? variation.valorMensal ?? 0,
  )

  if (!recorrente && installmentValue <= 0) {
    const projectTotal = variation.items
      .filter((i) => i.tipo === 'avulso')
      .reduce((acc, i) => acc + Number(i.total ?? 0), 0)
    installmentValue =
      installments > 0 ? Math.max(0, (projectTotal - entry) / installments) : projectTotal
  }

  let total = Number(explicit?.total ?? 0)
  if (!Number.isFinite(total) || total <= 0) {
    if (recorrente) {
      total = entry + installmentValue * installments + setupTotal
    } else {
      const projectTotal = variation.items
        .filter((i) => i.tipo === 'avulso')
        .reduce((acc, i) => acc + Number(i.total ?? 0), 0)
      total = projectTotal > 0 ? projectTotal : entry + installmentValue * installments
    }
  }

  return {
    recorrente,
    total,
    entry,
    installments,
    installmentValue,
    setupTotal,
  }
}

export function buildDefaultComboPricing(
  variation: ComboVariationWithPricing,
): ComboVariationPricing {
  const resolved = resolveComboVariationPricing(variation)
  return {
    recorrente: resolved.recorrente,
    total: resolved.total,
    entry: resolved.entry,
    installments: resolved.installments,
    installmentValue: resolved.installmentValue,
  }
}

export function buildPaymentDetailsFromComboVariation(
  variation: ComboVariationWithPricing,
): string {
  const { recorrente, entry, installments, installmentValue, setupTotal } =
    resolveComboVariationPricing(variation)

  const parts: string[] = []
  if (entry > 0) parts.push(`Entrada: ${formatCurrency(entry)}`)
  if (installments > 0 && installmentValue > 0) {
    parts.push(`${installments}x de ${formatCurrency(installmentValue)}`)
  }
  if (setupTotal > 0) parts.push(`setup ${formatCurrency(setupTotal)}`)

  if (parts.length > 0) return parts.join(' + ')

  if (recorrente && installmentValue > 0) {
    return `${formatCurrency(installmentValue)}/mês`
  }
  return formatCurrency(resolveComboVariationPricing(variation).total)
}

export function buildInvestmentLabelFromComboVariation(
  variation: ComboVariationWithPricing,
): string {
  const { recorrente, total, installmentValue } =
    resolveComboVariationPricing(variation)

  if (recorrente && installmentValue > 0) {
    if (total > installmentValue * 1.05) {
      return formatCurrency(total)
    }
    return `${formatCurrency(installmentValue)}/mês`
  }
  return formatCurrency(total)
}

export function buildLoyaltyLabelFromComboVariation(
  variation: ComboVariationWithPricing,
): string {
  const { recorrente, installments } = resolveComboVariationPricing(variation)
  if (recorrente) return `Fidelidade: ${installments} meses`
  return `Prazo: ${installments} meses`
}

/** Variação com 2+ itens — tratar como pacote único no Anexo 1. */
export function pickPrimaryComboVariation(
  variations: ComboVariationWithPricing[],
  items: PropostaItem[],
): ComboVariationWithPricing | null {
  if (variations.length === 0) return null

  const itemIds = new Set(items.map((i) => i.id))
  const matching = variations.filter((v) =>
    v.itemIds.length >= 2 && v.itemIds.every((id) => itemIds.has(id)),
  )

  if (matching.length === 1) return matching[0]
  if (variations.length === 1 && variations[0].itemIds.length >= 2) return variations[0]

  const multiItem = variations.find((v) => v.itemIds.length >= 2)
  return multiItem ?? null
}

export function listOrphanItemsForCombo(
  items: PropostaItem[],
  variation: ComboVariationWithPricing | null,
): PropostaItem[] {
  if (!variation) return items
  const inCombo = new Set(variation.itemIds)
  return items.filter((i) => !inCombo.has(i.id))
}
